import { Column } from '@tanstack/react-table';

import { DatePickerInput, TextInput } from '../../../Forms';
import { Link } from '../../../Link';
import { WithLightTitle } from '../../../WithLightTitle';

import styles from './RangeFilter.module.scss';
import { RangeFilterProps } from './RangeFilter.types';

/** Sometimes the getFacetedMinMaxValues returns an undefined value */
const manuallyComputeMinMax = <TData,>(column: Column<TData>): [number, number] | undefined => {
  const minMax = column.getFacetedMinMaxValues()?.flat() as
    | [number | undefined, number | undefined]
    | undefined;
  if (!minMax) return undefined;
  if (minMax.length === 2 && minMax[0] !== undefined && minMax[1] !== undefined)
    return minMax as [number, number];
  const uniqueValues = [...column.getFacetedUniqueValues().keys()].filter(
    value => value !== undefined
  ) as number[];
  if (!uniqueValues.length) return undefined;
  return [Math.min(...uniqueValues), Math.max(...uniqueValues)];
};

export const RangeFilter = <TData, TValue>({
  column,
  filterValue,
  setFilterValue,
}: RangeFilterProps<TData, TValue>) => {
  const minMax = manuallyComputeMinMax(column);
  if (!minMax) return null;
  if (column.columnDef.meta?.isDate)
    return (
      <>
        <div className={styles.buttons}>
          <Link size="xsmall" onClick={() => setFilterValue(undefined)} disabled={!filterValue}>
            réinitialiser
          </Link>
        </div>
        <WithLightTitle title="depuis le" className={styles.label}>
          <DatePickerInput
            size="medium"
            showYearDropdown
            minDate={new Date(minMax[0])}
            maxDate={new Date(filterValue?.[1] ?? minMax[1])}
            selected={new Date(filterValue?.[0] ?? minMax[0])}
            isClearable={filterValue?.[0] !== minMax[0]}
            onChange={date =>
              setFilterValue((filterValue?: [number, number]) => {
                return [date?.valueOf() ?? minMax[0], filterValue?.[1] ?? minMax[1]];
              })
            }
          />
        </WithLightTitle>
        <WithLightTitle title="jusqu'au" className={styles.label}>
          <DatePickerInput
            size="medium"
            showYearDropdown
            minDate={new Date(filterValue?.[0] ?? minMax[0])}
            maxDate={new Date(minMax[1])}
            selected={new Date(filterValue?.[1] ?? minMax[1])}
            isClearable={filterValue?.[1] !== minMax[1]}
            onChange={date =>
              setFilterValue((filterValue?: [number, number]) => {
                return [filterValue?.[0] ?? minMax[0], date?.valueOf() ?? minMax[1]];
              })
            }
          />
        </WithLightTitle>
      </>
    );
  return (
    <>
      <div className={styles.buttons}>
        <Link size="xsmall" onClick={() => setFilterValue(undefined)} disabled={!filterValue}>
          réinitialiser
        </Link>
      </div>
      <WithLightTitle title="minimum" className={styles.label}>
        <TextInput
          size="medium"
          type="number"
          style={{ minWidth: 0 }}
          value={filterValue?.[0] ?? minMax[0]}
          onChange={event =>
            setFilterValue((filterValue?: [number, number]) => {
              return [
                parseInt((event.target as HTMLInputElement).value),
                filterValue?.[1] ?? minMax[1],
              ];
            })
          }
        />
      </WithLightTitle>
      <WithLightTitle title="maximum" className={styles.label}>
        <TextInput
          size="medium"
          type="number"
          style={{ minWidth: 0 }}
          value={filterValue?.[1] ?? minMax[1]}
          onChange={event =>
            setFilterValue((filterValue?: [number, number]) => {
              return [
                filterValue?.[0] ?? minMax[0],
                parseInt((event.target as HTMLInputElement).value),
              ];
            })
          }
        />
      </WithLightTitle>
    </>
  );
};
